import { Card, CardHeader, CardBody, Container, Collapse, Col, Row } from "reactstrap";
import React, { useState } from "react";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import SimpleHeader from "@/components/Headers/SimpleHeader";

export type GlobalToolAccordionProps<T> = {
  /** SimpleHeader name, e.g. "Designs". */
  headerName: string;
  /** List card title in the editor layout. */
  listTitle: string;
  /** List card title in the read-only layout (the designs screen capitalises it differently). */
  readOnlyListTitle: string;
  /** Lower-case noun after "Update <name>" / "Create ". */
  noun: string;
  /** What "Update <…>" shows for the row being edited. */
  titleOf: (elem: T) => React.ReactNode;
  emptyElem: T;
  renderList: (handleUpdate: (elem: T) => void) => React.ReactNode;
  renderCreate: (backToList: () => void) => React.ReactNode;
  renderUpdate: (elem: T, backToList: () => void) => React.ReactNode;
  /** Called when a closed card opens and again after back-to-list (the templates screen resets its settings there). */
  onReset?: () => void;
};

/**
 * The GlobalTools index screens (designs, variables, templates; SCRUM-2118):
 * a list card and an edit card (create, or update after a row's edit button)
 * side by side, or the list alone for a user who can neither publish nor
 * edit. The three screens were one component copied three times; they now pass
 * their differences in. Markup pinned by
 * tests/integration/globalTools/globalToolsIndex.snapshots.test.tsx.
 */
function GlobalToolAccordion<T>({
  headerName,
  listTitle,
  readOnlyListTitle,
  noun,
  titleOf,
  emptyElem,
  renderList,
  renderCreate,
  renderUpdate,
  onReset,
}: GlobalToolAccordionProps<T>) {
  const [openedCollapses, setOpenedCollapses] = useState(["displayCard"]);
  const permission = useCurrentPermission();
  const [openUpdateCard, setOpenUpdateCard] = useState(false);
  const [elemToUpdate, setElemToUpdate] = useState<T>(emptyElem);
  const collapsesToggle = (collapse: string) => {
    collapse === "displayCard" && setOpenUpdateCard(false);
    if (!openedCollapses.includes(collapse)) {
      onReset?.();
      setOpenedCollapses([collapse]);
    }
  };

  const handleUpdate = (elem: T) => {
    collapsesToggle("editCard");
    setOpenUpdateCard(true);
    setElemToUpdate(elem);
  };
  const backToList = () => {
    collapsesToggle("displayCard");
    onReset?.();
  };
  const isOpen = (collapse: string) => openedCollapses.includes(collapse);

  return (
    <div>
      <SimpleHeader name={headerName} parentName="Global Tools" />

      {permission.publish || permission.edit ? (
        <div className="accordion">
          <Container className="mt--6" fluid>
            <Row>
              <Col className={isOpen("displayCard") ? "col-md-10" : "col-md-2"}>
                <Card className="card-plain">
                  <CardHeader
                    className="text-center"
                    role="tab"
                    onClick={() => collapsesToggle("displayCard")}
                    aria-expanded={isOpen("displayCard")}
                  >
                    <h3 className="mb-0">{listTitle}</h3>
                  </CardHeader>
                  <Collapse role="tabpanel" isOpen={isOpen("displayCard")}>
                    <CardBody>{renderList(handleUpdate)}</CardBody>
                  </Collapse>
                </Card>
              </Col>

              <Col className={isOpen("editCard") ? "col-md-10" : "col-md-2"}>
                <Card className="card-plain">
                  <CardHeader
                    className="text-center"
                    role="tab"
                    onClick={() => collapsesToggle("editCard")}
                    aria-expanded={isOpen("editCard")}
                  >
                    <h3 className="mb-0">
                      {openUpdateCard ? (
                        <>
                          Update{" "}
                          <span className="text-primary">{titleOf(elemToUpdate)}</span>
                        </>
                      ) : (
                        "Create "
                      )}
                      <span> {noun}</span>
                    </h3>
                  </CardHeader>
                  <Collapse role="tabpanel" isOpen={isOpen("editCard")}>
                    <div style={{ paddingBottom: "30px" }}>
                      <CardBody className="text-center">
                        {openUpdateCard ? renderUpdate(elemToUpdate, backToList) : renderCreate(backToList)}
                      </CardBody>
                    </div>
                  </Collapse>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      ) : (
        <Container className="mt--6" fluid>
          <Row>
            <Col className="col-md-12">
              <Card className="card">
                <CardHeader className="text-center">
                  <h3 className="mb-0">{readOnlyListTitle}</h3>
                </CardHeader>
                <CardBody>{renderList(handleUpdate)}</CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
    </div>
  );
}

export default GlobalToolAccordion;
